function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia(element) {
  const target = element || document;

  target.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  target.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  target.querySelectorAll('video').forEach((video) => {
    video.parentElement.dataset.play = false;
    video.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

function observeDOM(selector, callback, options = {}) {
  const observer = new MutationObserver(() => {
    const elements = document.querySelectorAll(selector);

    if (elements.length) {
      callback(elements, observer);
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
    ...options,
  });

  return observer;
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

/* CUSTOM */

const isIntersecting = (element, callback, rootMargin = '150px') => {
  new IntersectionObserver(([e], o) => {
    if (e.isIntersecting) {
      if (callback) callback();

      e.target.dataset.intersected = true;
      o.disconnect();
    }
  }, { rootMargin }).observe(element);
};

const onUserInteraction = callback => {
  let triggered = false;
  const actions = ['click', 'touchmove', 'mousemove', 'keydown', 'scroll'];

  actions.forEach(action => {
    document.addEventListener(action, () => {
      if (triggered) return;

      triggered = true;
      callback();
    });
  });
};

const waitForElemObject = (name, callback, elem = window) => {
  if (elem[name]) {
    callback();
    return;
  }

  setTimeout(() => waitForElemObject(name, callback, elem), 100);
};

const moneyFormat = (cents, format = window.money_format) => {
  if (typeof cents == 'string') cents = cents.replace('.', '');

  let value = '';
  let formatString = format;
  let placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

  function defaultOption(opt, def) {
    return (typeof opt == 'undefined' ? def : opt);
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    decimal = defaultOption(decimal, '.');
    precision = defaultOption(precision, 2);
    thousands = defaultOption(thousands, ',');

    if (isNaN(number) || number == null) { return 0; }

    number = (number/100.0).toFixed(precision);

    let parts = number.split('.'),
      dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
      cents   = parts[1] ? (decimal + parts[1]) : '';

    return dollars + cents;
  }

  switch(formatString.match(placeholderRegex)[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
  }

  return formatString.replace(placeholderRegex, value);
};

class CardProduct extends HTMLElement {
  constructor() {
    super();

    isIntersecting(this);
    this.marketsCount = this.dataset.marketsCount;
    this.setListeners();
  }

  setListeners() {
    if (this.marketsCount > 0) {
      const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

      const markets = this.querySelector('.card__markets');
      const shopBtn = this.querySelector('.card__button--shop');

      shopBtn?.addEventListener('click', e => this.toggleMarkets(shopBtn, markets, e));

      if (!isTouch) {
        shopBtn?.addEventListener('mouseenter', e => this.toggleMarkets(shopBtn, markets, e));
      }
    }
  }

  toggleMarkets(btn, markets, e) {
    e.preventDefault();

    btn.classList.toggle('card__button--active');
    markets.classList.toggle('card__markets--active');
  }
}

customElements.define('card-product', CardProduct);

class QuantityInput extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('input');
    this.plus = this.querySelector('.plus');
    this.minus = this.querySelector('.minus');

    this.min = this.input?.dataset.min || 1;

    this.plus?.addEventListener('click', this.handleClick.bind(this));
    this.minus?.addEventListener('click', this.handleClick.bind(this));
  }

  handleClick(e) {
    e.preventDefault();

    if (e.currentTarget.name === 'plus') {
      this.input.stepUp();
    } else {
      this.input.stepDown();
    }

    if (this.input.value == this.min) {
      this.minus.classList.add('disabled');
    } else {
      this.minus.classList.remove('disabled');
    }
  }
}

customElements.define('quantity-input', QuantityInput);

class CardProductQuantityInput extends QuantityInput {
  constructor() {
    super();

    this.id = this.dataset.id;
    this.errorListenerCreated = false;

    this.form = this.closest('form');
    this.error = this.querySelector('.error');
  }

  handleClick(e) {
    const prevValue = this.input.value;

    super.handleClick(e);

    if (!this.errorListenerCreated) {
      this.errorListenerCreated = true;

      document.addEventListener(`cart-error-${ this.id }`, e => {
        const { detail: { status, description } } = e;

        const regex = /\d+/;
        const count = description.match(regex);

        if (status === 422 && count) {
          this.input.value = count[0];
        } else {
          this.input.value = prevValue;
        }

        this.error.innerText = description;

        if (this.input.value == 0) {
          this.minus.classList.add('disabled');
        }

        setTimeout(() => this.error.innerText = '', 6000);
      });
    }

    if (e.currentTarget.name === 'plus') {
      document.dispatchEvent(new CustomEvent('cart:add', {
        detail: {
          el: this,
          id: this.id,
          items: [{ id: this.id, quantity: 1 }]
        }
      }));
    } else {
      document.dispatchEvent(new CustomEvent('cart:update', {
        detail: {
          el: this,
          id: this.id,
          updates: { [this.id]: parseInt(this.input.value, 10) }
        }
      }));
    }
  }
}

customElements.define('card-quantity-input', CardProductQuantityInput);

class FreeShippingBar extends HTMLElement {
  constructor() {
    super();

    waitForElemObject('Shopify', this.init.bind(this));
  }

  init() {
    if (window.Shopify?.currency && window.Shopify?.currency?.active === 'USD') {
      this.classList.remove('not-ready');
      return;
    }

    const rate = Number(window.Shopify.currency.rate).toFixed(2);

    this.text = this.dataset.text;
    this.textDone = this.dataset.textDone;
    this.subtotal = this.dataset.subtotal;
    this.amount = this.dataset.amount.slice(0, -2) * rate * 100;

    this.textEl = this.querySelector('.free-shipping__content');
    this.backgroundEl = this.querySelector('.free-shipping__background');

    const moneyClear = this.amount - this.subtotal;
    const moneyHtml = this.text.replace('[spend-x]', moneyFormat(moneyClear));

    let percent = this.subtotal * 100 / this.amount;

    if (moneyClear < 0) percent = 100;

    this.backgroundEl.style.width = `${ percent }%`;
    this.classList[moneyClear > 0 ? 'remove' : 'add']('free-shipping--complete');
    this.textEl.innerHTML = moneyClear > 0 ? moneyHtml : this.textDone;
    this.classList.remove('not-ready');
  }
}

customElements.define('free-shipping-bar', FreeShippingBar);

class HeaderMenu extends HTMLElement {
  constructor() {
    super();

    this.breakpoint = 1024;

    this.menu = this.querySelector('.header-menu');
    this.trigger = document.querySelector('.header-menu__trigger');
    this.items = this.querySelectorAll('.header-menu__item--level-1');
    this.close = this.querySelector('.header-menu__close');
    this.overlay = document.querySelector('.header-menu__overlay');
    this.back = this.querySelectorAll('.header-menu__back');

    this.selectedIndex = null;

    if (!this.menu || !this.items.length) return;

    this.#init();
  }

  #init() {
    this.#bindItems();
    this.#bindTrigger();
    this.#bindResize();
  }

  #bindItems() {
    this.items.forEach((item, index) => {
      const link = item.querySelector('.header-menu__link');
      const submenu = item.querySelector('.header-menu__submenu');

      if (!link || !submenu) return;

      link.addEventListener('mouseenter', () => {
        if (!this.#isDesktop()) return;
        this.#open(index);
        this.#toggleScroll(true);
      });

      item.addEventListener('mouseleave', () => {
        if (!this.#isDesktop()) return;
        this.#close();
      });

      link.addEventListener('click', e => {
        if (this.#isDesktop()) return;
        e.preventDefault();

        if (this.selectedIndex === index) {
          this.#close();
        } else {
          this.#open(index);

          requestAnimationFrame(() => {
            this.#scrollToTop();
          });
        }
      });
    });
  }

  #bindTrigger() {
    if (!this.trigger) return;

    this.trigger.addEventListener('click', () => {
      const isOpen = this.menu.classList.toggle('header-menu--active');
      this.#toggleScroll(isOpen);
    });

    this.close.addEventListener('click', () => this.#close());
    this.overlay.addEventListener('click', () => this.#close());

    this.back.forEach(button => {
      button.addEventListener('click', e => {
        e.preventDefault();

        this.#closeSubmenu();

        if (!this.#isDesktop()) {
          requestAnimationFrame(() => {
            this.#scrollToTop(this.menu);
          });
        }
      });
    });
  }

  #bindResize() {
    window.addEventListener('resize', () => {
      if (this.#isDesktop()) {
        this.#close();
      }
    });
  }

  #open(index) {
    this.selectedIndex = index;

    this.items.forEach((item, i) => {
      const submenu = item.querySelector('.header-menu__submenu');

      const isActive = i === index;

      item.classList.toggle('header-menu__item--opened', isActive);

      if (submenu) {
        submenu.classList.toggle('header-menu__submenu--open', isActive);
      }
    });

    if (!this.#isDesktop()) {
      const openedItem = this.items[index];
      const submenu = openedItem?.querySelector('.header-menu__submenu');

      requestAnimationFrame(() => {
        this.#scrollToTop(submenu);
      });
    }
  }

  #close() {
    this.#closeSubmenu();
    this.menu.classList.remove('header-menu--active');
    this.#toggleScroll(false);
  }

  #closeSubmenu() {
    this.selectedIndex = null;

    this.items.forEach(item => {
      item.classList.remove('header-menu__item--opened');

      const submenu = item.querySelector('.header-menu__submenu');

      submenu?.classList.remove('header-menu__submenu--open');
    });
  }

  #toggleScroll(state) {
    document.documentElement.classList.toggle('menu-active', state);
    document.body.classList.toggle('menu-active', state);
  }

  #scrollToTop(target) {
    if (this.#isDesktop() || !target) return;

    target.scrollTo({
      top: 0,
      behavior: 'auto',
    });
  }

  #isDesktop() {
    return window.innerWidth > this.breakpoint;
  }
}

customElements.define('header-menu', HeaderMenu);
